import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useTranslation } from 'react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import i18n from '@src/languages';
import { Button, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { PACK_DURATION, PACKAGE_LEVEL_V3 } from '@src/constants/package';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { VOUCHER_TYPE } from '@src/constants/voucher';
import { checkCustomPackage } from '@src/services/package';

const formatNumber = (num, language = 'en') => {
  const suffixes = {
    en: {
      K: 'K',
      M: 'M',
      B: 'B',
    },
    vi: {
      K: 'K',
      M: ' triệu',
      B: ' tỷ',
    },
  };

  const langSuffixes = suffixes[language] || suffixes.en;
  const format = (value, suffix) =>
    `${value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}${suffix}`;

  if (num >= 1e9) return format(num / 1e9, langSuffixes.B);
  if (num >= 1e6) return format(num / 1e6, langSuffixes.M);
  if (num >= 1e3) return format(num / 1e3, langSuffixes.K);
  return num.toString();
};

const FreeAmountCredits = ({
  packageCode,
  introductoryPeriodDays,
  regularDailyCredits,
  introductoryDailyCredits,
  newPricingOverviewEnabled,
  hideDot,
}) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const [openPopup, setOpenPopup] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClickShowPopup = () => {
    if (isMobile) setOpenPopup(true);
    return null;
  };

  const showTooltip = regularDailyCredits !== introductoryDailyCredits;

  return (
    <div className="free-amount-credits">
      <div className="free-amount-credits-item">
        {!hideDot && (
          <FiberManualRecordRoundedIcon className="fiber-manual-record-icon" />
        )}
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t(
                newPricingOverviewEnabled
                  ? 'introductoryDailyCreditsV2'
                  : 'introductoryDailyCredits',
                {
                  introductoryDailyCredits:
                    introductoryDailyCredits?.toLocaleString(language),
                },
              ),
            ),
          }}
        />
      </div>
      {showTooltip && (
        <Tooltip
          arrow
          data-id="tooltip-plan-compare"
          data-package-code={packageCode}
          title={
            <Typography
              component="span"
              sx={{ fontSize: '14px' }}
              style={{ textAlign: 'left' }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t(
                    newPricingOverviewEnabled
                      ? 'introductoryPeriodDaysV2'
                      : 'introductoryPeriodDays',
                    {
                      introductoryPeriodDays,
                      regularDailyCredits:
                        regularDailyCredits?.toLocaleString(language),
                      introductoryDailyCredits:
                        introductoryDailyCredits?.toLocaleString(language),
                    },
                  ),
                ),
              }}
            />
          }
          placement="top"
        >
          <InfoRoundedIcon
            onClick={handleClickShowPopup}
            className="info-out-line-icon"
          />
        </Tooltip>
      )}
      <NotificationDialogV3
        name={t('detailedInformation')}
        title={t('detailedInformation')}
        description={t(
          newPricingOverviewEnabled
            ? 'introductoryPeriodDaysV2'
            : 'introductoryPeriodDays',
          {
            introductoryPeriodDays,
            regularDailyCredits: regularDailyCredits?.toLocaleString(language),
            introductoryDailyCredits:
              introductoryDailyCredits?.toLocaleString(language),
          },
        )}
        open={openPopup && isMobile}
        onClose={() => setOpenPopup(false)}
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopup(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </div>
  );
};

const PurchaseAmountCredits = ({
  creditsPerCycle,
  totalCredits,
  duration,
  isPackageVip,
  totalCyclesWithVoucher,
  newPricingOverviewEnabled,
  hideDot,
}) => {
  const { t } = useTranslation();
  return (
    <div className="purchase-amount-credits">
      {!hideDot && (
        <FiberManualRecordRoundedIcon className="fiber-manual-record-icon" />
      )}
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(
            t(
              newPricingOverviewEnabled
                ? 'amountCreditsPerCycleV2'
                : 'amountCreditsPerCycle',
              {
                creditsPerCycle,
              },
            ),
          ),
        }}
      />
      {totalCredits && (
        <span>
          {' '}
          (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t('xMonth', {
                  totalCredits,
                  duration,
                  value: totalCyclesWithVoucher,
                }),
              ),
            }}
          />
          )
        </span>
      )}
      {isPackageVip && !newPricingOverviewEnabled && <span> (*)</span>}
    </div>
  );
};

const AmountCredits = ({
  pkg,
  bestVoucher,
  newPricingOverviewEnabled,
  hideDot,
  usageLimit,
}) => {
  // Free package
  const { language } = i18n;
  const hasFreemiumConfig = pkg?.freemiumConfig;
  if (hasFreemiumConfig) {
    return (
      <FreeAmountCredits
        packageCode={pkg?.code}
        introductoryPeriodDays={pkg?.freemiumConfig?.introductoryPeriodDays}
        introductoryDailyCredits={pkg?.freemiumConfig?.introductoryDailyCredits}
        regularDailyCredits={pkg?.freemiumConfig?.regularDailyCredits}
        newPricingOverviewEnabled={newPricingOverviewEnabled}
        hideDot={hideDot}
      />
    );
  }

  // Purchase package
  const { creditsPerCycle, totalCycles } = pkg?.cycle || {};
  let totalCyclesWithVoucher = totalCycles;
  let totalCredits =
    pkg?.expiresIn !== PACK_DURATION.MONTH ? creditsPerCycle * totalCycles : 0;
  let creditsPerCycleWithVoucher = creditsPerCycle;

  if (bestVoucher?.type === VOUCHER_TYPE.TIME) {
    totalCyclesWithVoucher = totalCycles + (bestVoucher?.value || 0);
    if (pkg?.expiresIn === PACK_DURATION.MONTH) {
      totalCredits =
        totalCyclesWithVoucher > 1
          ? creditsPerCycleWithVoucher * totalCyclesWithVoucher
          : 0;
    } else {
      totalCredits = creditsPerCycleWithVoucher * totalCyclesWithVoucher;
    }
  }

  if (bestVoucher?.type === VOUCHER_TYPE.CHARACTER) {
    creditsPerCycleWithVoucher *= bestVoucher?.value || 1;
  }

  if (bestVoucher?.type === VOUCHER_TYPE.FIXED_CHARACTER) {
    const isMonthlyPackage = pkg?.expiresIn === PACK_DURATION.MONTH;
    const isCustomPackage = checkCustomPackage({
      type: pkg?.type,
      level: pkg?.level,
    });
    if (isMonthlyPackage || isCustomPackage) {
      creditsPerCycleWithVoucher += bestVoucher?.value || 0;
    } else {
      const fixedCharacterPerCycle = Math.ceil(
        bestVoucher?.value / totalCycles,
      );
      creditsPerCycleWithVoucher += fixedCharacterPerCycle;
    }
  }

  const isPackageVip = pkg?.level === PACKAGE_LEVEL_V3.VIP;

  if (usageLimit) {
    creditsPerCycleWithVoucher /= 2;
  }

  return (
    <PurchaseAmountCredits
      creditsPerCycle={
        creditsPerCycleWithVoucher
          ? formatNumber(creditsPerCycleWithVoucher, language)
          : null
      }
      totalCredits={totalCredits ? formatNumber(totalCredits, language) : null}
      isPackageVip={isPackageVip}
      totalCyclesWithVoucher={totalCyclesWithVoucher}
      newPricingOverviewEnabled={newPricingOverviewEnabled}
      hideDot={hideDot}
    />
  );
};

export default AmountCredits;
